/**
 * Colour space conversions: sRGB, linear RGB, CIE XYZ, CIE Lab.
 *
 * All conversions take and return 0..255 triples unless the name says
 * otherwise (`*Linear`, `*Norm` operate on 0..1). Keeping the interface in
 * 0..255 means `space.ts` composes with `apply.ts` without a rescale step.
 *
 * The matrices here are **column-major**, per core/mat3.ts. They are stored as
 * plain arrays and multiplied with explicit index arithmetic, because the sRGB
 * transfer function is nonlinear and cannot be folded into the affine anyway.
 *
 * @module core/space
 */

import type { Mat3, Vec3 } from './types.ts';
import { mulVec } from './mat3.ts';

/** D65 white point, 2-degree observer. */
export const D65: Vec3 = Float64Array.of(0.95047, 1.0, 1.08883);

/**
 * Linear sRGB to CIE XYZ (D65). Row-major as written, converted on load.
 *
 * Standard IEC 61966-2-1 values.
 */
const RGB_TO_XYZ_ROWS = [
  0.4124564, 0.3575761, 0.1804375,
  0.2126729, 0.7151522, 0.072175,
  0.0193339, 0.119192, 0.9503041,
];

/** Build a column-major matrix from row-major literals. */
function fromRows(rows: readonly number[]): Mat3 {
  const m = new Float64Array(9);
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) m[c * 3 + r] = rows[r * 3 + c]!;
  return m;
}

/**
 * XYZ -> linear sRGB. The published inverse of the matrix above (Bruce Lindbloom's
 * values), not a transpose: the sRGB primaries are not orthogonal, so the
 * transpose is only an approximation.
 */
const XYZ_TO_RGB_ROWS = [
  3.2404542, -1.5371385, -0.4985314,
  -0.969266, 1.8760108, 0.041556,
  0.0556434, -0.2040259, 1.0572252,
];

/** Linear sRGB -> XYZ. */
export const RGB_TO_XYZ: Mat3 = fromRows(RGB_TO_XYZ_ROWS);
/** XYZ -> linear sRGB. */
export const XYZ_TO_RGB: Mat3 = fromRows(XYZ_TO_RGB_ROWS);

/**
 * sRGB electro-optical transfer function: 0..1 encoded -> 0..1 linear.
 * The piecewise definition is the exact IEC curve, not a plain 2.2 power.
 */
export function srgbToLinear(v: number): number {
  const x = v / 255;
  const lin = x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  return lin;
}

/** Inverse of `srgbToLinear`, returning 0..255. */
export function linearToSrgb(v: number): number {
  const x = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return x * 255;
}

/** Linearise an 0..255 triple. */
export function toLinearRGB(rgb: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  o[0] = srgbToLinear(rgb[0]!);
  o[1] = srgbToLinear(rgb[1]!);
  o[2] = srgbToLinear(rgb[2]!);
  return o;
}

/** Encode a linear triple back to 0..255. */
export function fromLinearRGB(linear: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  o[0] = linearToSrgb(linear[0]!);
  o[1] = linearToSrgb(linear[1]!);
  o[2] = linearToSrgb(linear[2]!);
  return o;
}

/** 0..255 sRGB triple -> CIE XYZ (D65, Y in 0..1). */
export function rgbToXyz(rgb: ArrayLike<number>): Vec3 {
  return mulVec(RGB_TO_XYZ, toLinearRGB(rgb));
}

/** CIE XYZ -> 0..255 sRGB triple, clamped. */
export function xyzToRgb(xyz: ArrayLike<number>): Vec3 {
  const linear = mulVec(XYZ_TO_RGB, xyz);
  const out = fromLinearRGB(linear);
  out[0] = clamp255(out[0]!);
  out[1] = clamp255(out[1]!);
  out[2] = clamp255(out[2]!);
  return out;
}

/** CIE Lab reference: `f(t)`. */
function labF(t: number): number {
  const delta = 6 / 29;
  return t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta * delta) + 4 / 29;
}

/** Inverse of `labF`. */
function labFInv(t: number): number {
  const delta = 6 / 29;
  return t > delta ? t ** 3 : 3 * delta * delta * (t - 4 / 29);
}

/** 0..255 sRGB -> CIE Lab (D65). `L` is 0..100, `a` and `b` are roughly -128..127. */
export function rgbToLab(rgb: ArrayLike<number>, out?: Vec3): Vec3 {
  const xyz = rgbToXyz(rgb);
  const fx = labF(xyz[0]! / D65[0]!);
  const fy = labF(xyz[1]! / D65[1]!);
  const fz = labF(xyz[2]! / D65[2]!);
  const o = out ?? new Float64Array(3);
  o[0] = 116 * fy - 16;
  o[1] = 500 * (fx - fy);
  o[2] = 200 * (fy - fz);
  return o;
}

/** CIE Lab -> 0..255 sRGB, clamped. */
export function labToRgb(lab: ArrayLike<number>): Vec3 {
  const fy = (lab[0]! + 16) / 116;
  const fx = fy + lab[1]! / 500;
  const fz = fy - lab[2]! / 200;
  const xyz = Float64Array.of(
    D65[0]! * labFInv(fx),
    D65[1]! * labFInv(fy),
    D65[2]! * labFInv(fz),
  );
  return xyzToRgb(xyz);
}

/** CIE76 colour difference. Cheap, and good enough for a proxy metric. */
export function deltaE76(a: ArrayLike<number>, b: ArrayLike<number>): number {
  const dr = a[0]! - b[0]!;
  const dg = a[1]! - b[1]!;
  const db = a[2]! - b[2]!;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

/** Rec. 709 luma of an 0..255 sRGB triple. */
export function luma709(rgb: ArrayLike<number>): number {
  return 0.2126 * rgb[0]! + 0.7152 * rgb[1]! + 0.0722 * rgb[2]!;
}

/**
 * CIE Lab chroma `sqrt(a^2 + b^2)`.
 * The pigment mask uses `a* > 12`; see `core/metrics.ts`.
 */
export function chroma(lab: ArrayLike<number>): number {
  return Math.hypot(lab[1]!, lab[2]!);
}

/** Rotate into a decorrelated space: `Y`, then the two chroma axes of Lab. */
export function rgbToOpponent(rgb: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  o[0] = luma709(rgb);
  o[1] = rgb[0]! - rgb[1]!;
  o[2] = rgb[1]! - rgb[2]!;
  return o;
}

/**
 * The linear part of the RGB -> Lab transform: RGB to XYZ.
 *
 * The full Lab transform is nonlinear (the `f(t)` cube root), so it cannot be
 * an affine. The Lab-class presets therefore go through the 3D LUT of T3b,
 * using this matrix only as their linear starting point.
 */
export function labApproxAffine(): Mat3 {
  return Float64Array.from(RGB_TO_XYZ);
}

/**
 * Evaluator suitable for `build3dLut`, giving Lab as a normalised triple.
 * `L` is mapped to `0..1`, `a` and `b` are mapped from `-128..127` to `0..1`.
 */
export function rgbToLabNormalised(r: number, g: number, b: number): Vec3 {
  const lab = rgbToLab(Float64Array.of(r * 255, g * 255, b * 255));
  return Float64Array.of(
    lab[0]! / 100,
    (lab[1]! + 128) / 255,
    (lab[2]! + 128) / 255,
  );
}

function clamp255(v: number): number {
  if (!(v > 0)) return 0;
  return v > 255 ? 255 : v;
}
